import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';
import { types as sdkTypes } from '../../../../util/sdkLoader';

// Import shared components
import { H3, ListingLink } from '../../../../components';
import EditListingMoreInfoForm from './EditListingMoreInfoForm';
import EditListingLayoutSideNav from '../EditListingLayoutSideNav/EditListingLayoutSideNav';

// Import modules from this directory
import css from './EditListingMoreInfoPanel.module.css';
import { getSubTypes, isArrayLength } from '../../../../util/genericHelpers';

const { Money } = sdkTypes;

const filterEndorsements = endorsements => {
  return endorsements
    .filter(endorsement => endorsement.platform && endorsement.type) // Ensure that endorsement has platform and type
    .map(({ platform, type, selected, price }) => ({
      platform,
      type,
      selected,
      price,
    }));
};

export const mapFilteredServices = services => {
  return services
    .filter(service => !service.types) // Filter out services with 'types' array
    .map(service => {
      // Return the desired properties
      return {
        service: service.service,
        type: service.type,
        selected: service.selected,
        price: service.price,
      };
    });
};

const getInitialValues = params => {
  const { listing } = params;
  const { publicData = {} } = listing?.attributes || {};
  const {
    appearances,
    autographs,
    shoutouts,
    endorsements,
    talentPlus = null,
    creativeMedia = null,
  } = publicData || {};
  return {
    appearances,
    autographs,
    shoutouts,
    creativeMedia,
    endorsements,
    talentPlus,
  };
};

const EditListingMoreInfoPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    marketplaceCurrency,
    listingMinimumPriceSubUnits,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    config,
    onNextTab,
    isTalentPlus,
    viewport,
  } = props;

  const isMobileLayout = viewport.width && viewport.width > 0 && viewport.width < 1024;
  const listingTitle = listing?.attributes?.title;
  const classes = classNames(rootClassName || css.root, className);
  const initialValues = getInitialValues(props);

  const isPublished = listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;
  const priceCurrencyValid =
    marketplaceCurrency && initialValues.price instanceof Money
      ? initialValues.price.currency === marketplaceCurrency
      : !!marketplaceCurrency;
  const unitType = listing?.attributes?.publicData?.unitType;

  const handleSubmit = async values => {
    try {
      // Destructure input values with defaults
      const {
        appearances = [],
        autographs = {},
        shoutouts = [],
        talentPlus = [],
        creativeMedia = [],
        endorsements = [],
      } = values || {};

      // Process endorsements
      const filteredEndorsements = isArrayLength(endorsements)
        ? filterEndorsements(endorsements)
        : null;

      // Generate subtype arrays
      const autographsSubTypes = autographs.typeOfItems
        ? Object.keys(autographs.typeOfItems).filter(key => autographs.typeOfItems[key] === true)
        : [];
      const shoutoutsSubTypes = getSubTypes(shoutouts, 'option');
      const creativeMediaSubTypes = getSubTypes(creativeMedia, 'title');
      const endorsementsSubTypes = getSubTypes(endorsements, 'platform');
      const appearancesSubTypes = getSubTypes(appearances, 'title');
      const subtypeLabelMap = {
        photo: 'Photo',
        tradingCard: 'Trading Card',
        poster: 'Poster',
        showFanProvidedItems: 'Fan or Brand Provided Item',
        custom: 'Custom Talent Provided',
      };

      const autographSubtypeLabels =
        isArrayLength(autographsSubTypes) &&
        autographsSubTypes.map(type => subtypeLabelMap[type] || type);

      // Construct updateValues with publicData
      const updateValues = {
        price: new Money(0, 'USD'),
        publicData: {
          appearances,
          autographs,
          shoutouts,
          creativeMedia,
          endorsements: filteredEndorsements,
          isSignatureServices: true,

          // Conditionally include subtypes and flags
          ...(isArrayLength(autographsSubTypes) && {
            autographsSubTypes: autographSubtypeLabels,
            isAutograph: true,
          }),
          ...(isArrayLength(shoutoutsSubTypes) && { shoutoutsSubTypes, isShoutout: true }),
          ...(isArrayLength(endorsementsSubTypes) && { endorsementsSubTypes, isEndorsed: true }),
          ...(isArrayLength(creativeMediaSubTypes) && {
            creativeMediaSubTypes,
            isCreativeMedia: true,
          }),
          ...(isArrayLength(appearancesSubTypes) && { appearancesSubTypes, isAppearance: true }),
        },
      };

      // Add talentPlus if applicable
      if (isTalentPlus && talentPlus) {
        updateValues.publicData.talentPlus = mapFilteredServices(talentPlus);
      }

      // Submit the updated values
      onSubmit(updateValues);
    } catch (error) {
      console.error('Error during form submission:', error);
    }
  };
  return (
    <div className={classes}>
      <div className={css.contentLayoutWrapper}>
        <EditListingLayoutSideNav listing={listing} />
        <div className={css.detailContent}>
          <H3 as="h1">
            {isPublished ? (
              <FormattedMessage
                id="EditListingMoreInfoPanel.title"
                values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
              />
            ) : (
              <FormattedMessage
                id="EditListingMoreInfoPanel.createListingTitle"
                values={{ lineBreak: <br /> }}
              />
            )}
          </H3>
          <p
            className={css.detailDescription}
            dangerouslySetInnerHTML={{
              __html: `
      <strong>Get started offering Signature Services in a couple simple steps!</strong><br/><br/>
      1) Expand each service and place a check mark next to the services you wish to offer.<br/>
      2) Enter the price you would like to charge for each service.`,
            }}
          ></p>

          {priceCurrencyValid ? (
            <EditListingMoreInfoForm
              className={css.form}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              marketplaceCurrency={marketplaceCurrency}
              unitType={unitType}
              listingMinimumPriceSubUnits={listingMinimumPriceSubUnits}
              saveActionMsg={submitButtonText}
              disabled={disabled}
              isTalentPlus={isTalentPlus}
              ready={ready}
              updated={panelUpdated}
              updateInProgress={updateInProgress}
              fetchErrors={errors}
              onLiveSubmit={onSubmit}
            />
          ) : (
            <div className={css.priceCurrencyInvalid}>
              <FormattedMessage
                id="EditListingMoreInfoPanel.listingPriceCurrencyInvalid"
                values={{ marketplaceCurrency }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const { func, object, string, bool } = PropTypes;

EditListingMoreInfoPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingMoreInfoPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingMoreInfoPanel;
